import React, { useState, useEffect } from 'react';
import { User, LogOut, ChevronDown, Shield, Home, BarChart2, Play, LogIn, FileText, Newspaper, Plus, Lock, ArrowUp, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/subscription/SubscriptionContext';
import { useAudience } from '../context/AudienceContext';
import { useBuildAudience } from '../context/BuildAudienceContext';
import { useAudienceData } from '../contexts/AudienceContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import UserProfileDropdown from './UserProfileDropdown';
import { useRealtimeUsage } from '../hooks/useRealtimeUsage';
import { PLAN_TIERS } from '../types/subscription';
import { getZIndexClass } from '../config/zIndex';

const Sidebar: React.FC = () => {
  const { user, isAuthenticated, isAdmin, logout, isKnfUser, isInternalUser } = useAuth();
  const { currentSubscription, usage } = useSubscription();
  const { resetAudienceData } = useAudience();
  const { resetBuildAudienceData } = useBuildAudience();
  const { resetAudienceData: resetAudienceListData } = useAudienceData();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [audienceData, setAudienceData] = useState<{ count: number; total?: number; limit: number; can_create: boolean } | null>(null);
  const [hasSubmittedRequest, setHasSubmittedRequest] = useState<boolean>(false);
  const [hasReports, setHasReports] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user is from Panasonic domain (driving-only user)
  const drivingAllowedDomains = (import.meta.env.VITE_DRIVING_ALLOWED_DOMAINS || '').split(',').filter(Boolean);
  const userDomain = user?.email?.split('@')[1] || '';
  const isPanasonicUser = drivingAllowedDomains.includes(userDomain);

  // Users who can SEE the Build Audience nav item: admins only.
  const canAccessChatAndBuildAudience = isAdmin;

  // Which roles have Simulate/History gated behind owning an audience. This is a
  // SEPARATE question from nav visibility above — they were one expression until
  // Chat/Build-Audience was restricted to admins, which silently un-gated Simulate
  // and History for KNF (requiresAudience flipped true -> false). Kept as the
  // original `isAdmin || isKnfUser` so that gating is unchanged.
  const audienceGatedRoles = isAdmin || isKnfUser;

  // Check if user has any audiences based on the total from API
  // Panasonic users bypass this check - they can access Simulate/History without audiences
  const hasAudiences = isPanasonicUser || (audienceData !== null && (audienceData.total ?? 0) > 0);

  // Enable real-time usage updates
  useRealtimeUsage();
  
  // Check for submitted request state on mount
  useEffect(() => {
    const submittedState = localStorage.getItem('audience_request_submitted');
    if (submittedState === 'true') {
      setHasSubmittedRequest(true);
    }
  }, []);
  
  // Listen for audience submission events
  useEffect(() => {
    const handleAudienceSubmitted = () => {
      setHasSubmittedRequest(true);
      localStorage.setItem('audience_request_submitted', 'true');
      
      // Force a re-render by updating the component state
      setAudienceData(prev => prev ? {...prev} : null);
    };
    
    window.addEventListener('audience-submitted', handleAudienceSubmitted);
    return () => window.removeEventListener('audience-submitted', handleAudienceSubmitted);
  }, []);
  
  // Fetch audience count and limit
  useEffect(() => {
    const fetchAudienceData = async () => {
      if (isAuthenticated) {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/audience/count`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setAudienceData(data);
            
            // Reset submission state if user can create audiences again (count changed)
            if (data.can_create && hasSubmittedRequest) {
              setHasSubmittedRequest(false);
              localStorage.removeItem('audience_request_submitted');
            }
          }
        } catch (error) {
          console.error('Error fetching audience data:', error);
        }
      }
    };
    
    fetchAudienceData();
    // Refresh when location changes (e.g., after creating an audience)
    const interval = setInterval(fetchAudienceData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated, location.pathname]);

  // Fetch whether the user owns any reports (owner-scoped list). The Reports tab
  // shows only when the user has ≥1 report — for every user, including
  // admins/internal — so nobody ever lands on an empty Reports list.
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    (async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/reports`, {
          credentials: 'include',
        });
        if (response.ok && active) {
          const data = await response.json();
          setHasReports(Array.isArray(data) && data.length > 0);
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
      }
    })();
    return () => { active = false; };
  }, [isAuthenticated]);

  const handleLogout = async () => {
    // Reset all audience contexts before logout
    resetAudienceData();
    resetBuildAudienceData();
    resetAudienceListData();
    
    // Then logout
    await logout();
    setProfileMenuOpen(false);
    setIsMobileOpen(false);
    navigate('/auth');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogoClick = () => {
    // Reset all audience contexts when going home
    resetAudienceData();
    resetBuildAudienceData();
    resetAudienceListData();
    
    navigate('/');
  };

  const navigationItems = [
    {
      path: '/',
      icon: Home,
      label: 'Home',
      isActive: location.pathname === '/'
    },
    // Build Audience - admins only
    ...(canAccessChatAndBuildAudience ? [{
      path: '/build-audience',
      icon: Plus,
      label: 'Build Audience',
      isActive: location.pathname.startsWith('/build-audience'),
      isSpecial: true,
      audienceData: audienceData,
      hasSubmittedRequest: hasSubmittedRequest,
      currentSubscription: currentSubscription
    }] : []),
    {
      path: '/simulate',
      icon: Play,
      label: 'Simulate',
      isActive: location.pathname.startsWith('/simulate'),
      // Panasonic users can access Simulate without audiences
      requiresAudience: !isPanasonicUser && audienceGatedRoles
    },
    {
      path: '/analysis',
      icon: BarChart2,
      label: 'History',
      isActive: location.pathname === '/analysis' || location.pathname.startsWith('/analysis/'),
      // Panasonic users can access History without audiences
      requiresAudience: !isPanasonicUser && audienceGatedRoles
    }
  ];

  // Survey tab - admins and internal BluePill team members
  if (isAdmin || isInternalUser) {
    navigationItems.push({
      path: '/surveys',
      icon: FileText,
      label: 'Survey',
      isActive: location.pathname === '/surveys'
    });
  }

  if (hasReports) {
    navigationItems.push({
      path: '/reports',
      icon: Newspaper,
      label: 'Reports',
      isActive: location.pathname === '/reports' || location.pathname.startsWith('/reports/')
    });
  }

  if (isAdmin) {
    navigationItems.push({
      path: '/admin',
      icon: Shield,
      label: 'Admin',
      isActive: location.pathname === '/admin'
    });
  }

  const renderNavItem = (item: any) => {
    if (item.isSpecial) {
      // Special handling for Build Audience with limits
      const isEnterprise = currentSubscription?.id === PLAN_TIERS.ENTERPRISE;
      const isDisabled = audienceData?.can_create === false || (isEnterprise && hasSubmittedRequest);
      
      let tooltipMessage = 'Build a new audience';
      let IconComponent = item.icon;
      
      if (isDisabled) {
        if (isEnterprise && hasSubmittedRequest) {
          tooltipMessage = 'Request submitted - contact support for more audiences';
          IconComponent = ArrowUp;
        } else if (audienceData?.limit === 0) {
          tooltipMessage = 'Upgrade to create audiences';
          IconComponent = Lock;
        } else {
          tooltipMessage = `Audience limit reached (${audienceData?.count}/${audienceData?.limit})`;
          IconComponent = Lock;
        }
      }
      
      return (
        <Link 
          key={item.path}
          to={item.path} 
          className={`flex items-center py-2 px-4 transition-all duration-200 group relative ${
            isDisabled ? 'pointer-events-none' : ''
          }`}
          title={isExpanded ? '' : tooltipMessage}
        >
          <div className={`absolute inset-0 rounded-lg transition-all duration-200 ${
            item.isActive
              ? 'bg-[#E8FCFA]' 
              : isDisabled
              ? 'bg-gray-50'
              : 'group-hover:bg-gray-100'
          } ${
            isExpanded || isMobileOpen ? 'opacity-100' : 'opacity-0'
          }`} />
          
          <div className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 relative ${getZIndexClass('dropdown')} ${
            !isExpanded && !isMobileOpen && item.isActive ? 'bg-[#E8FCFA]' : ''
          } ${
            !isExpanded && !isMobileOpen && !item.isActive && !isDisabled ? 'group-hover:bg-gray-100' : ''
          }`}>
            <IconComponent className={`h-5 w-5 ${
              item.isActive ? 'text-[#07E5D1]' : isDisabled ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-900'
            }`} />
          </div>
          
          <span className={`ml-3 transition-opacity duration-200 whitespace-nowrap relative ${getZIndexClass('dropdown')} ${
            isExpanded || isMobileOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'
          } ${
            item.isActive ? 'text-[#07E5D1]' : isDisabled ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-900'
          }`}>
            {item.label}
          </span>
          {/* Only show count for non-enterprise users when expanded */}
          <span className={`ml-auto transition-opacity duration-200 ${isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
            {!isEnterprise && audienceData && audienceData.limit !== -1 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                audienceData.can_create
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {audienceData.count}/{audienceData.limit === 0 ? '0' : audienceData.limit}
              </span>
            )}
          </span>
        </Link>
      );
    }

    const IconComponent = item.icon;
    const isDisabled = item.requiresAudience && !hasAudiences;

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center py-2 px-4 transition-all duration-200 group relative ${
          isDisabled ? 'pointer-events-none' : ''
        }`}
        title={isExpanded ? '' : item.label}
        onClick={(e) => {
          if (isDisabled) {
            e.preventDefault();
          }
        }}
      >
        <div className={`absolute inset-0 rounded-lg transition-all duration-200 ${
          item.isActive
            ? 'bg-[#E8FCFA]'
            : isDisabled
            ? 'bg-gray-50'
            : 'group-hover:bg-gray-100'
        } ${
          isExpanded || isMobileOpen ? 'opacity-100' : 'opacity-0'
        }`} />

        <div className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 relative ${getZIndexClass('dropdown')} ${
          !isExpanded && !isMobileOpen && item.isActive ? 'bg-[#E8FCFA]' : ''
        } ${
          !isExpanded && !isMobileOpen && !item.isActive && !isDisabled ? 'group-hover:bg-gray-100' : ''
        }`}>
          <IconComponent className={`h-5 w-5 ${
            item.isActive ? 'text-[#07E5D1]' : isDisabled ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-900'
          }`} />
        </div>

        <span className={`ml-3 transition-opacity duration-200 whitespace-nowrap relative ${getZIndexClass('dropdown')} ${
          isExpanded || isMobileOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'
        } ${
          item.isActive ? 'text-[#07E5D1]' : isDisabled ? 'text-gray-400' : 'text-gray-600 group-hover:text-gray-900'
        }`}>
          {item.label}
        </span>
      </Link>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className={`fixed left-0 top-0 h-full bg-[#FAFBFC] border-r border-gray-200 shadow-sm ${getZIndexClass('popover')} transition-all duration-300 ${
        isExpanded ? 'w-64' : 'w-16'
      }`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-gray-200">
            <div 
              onClick={handleLogoClick} 
              className="flex items-center cursor-pointer transition-transform hover:scale-105"
            >
              {isExpanded ? (
                <img 
                  src={`${import.meta.env.VITE_PUBLIC_URL}/logo.png`} 
                  alt="Bluepill Logo" 
                  className="h-8 w-auto object-contain"
                />
              ) : (
                <img 
                  src={`${import.meta.env.VITE_PUBLIC_URL}/Bluepill Logomark (1).png`} 
                  alt="Bluepill Icon" 
                  className="h-8 w-8 object-contain"
                />
              )}
            </div>
          </div>
          
          {/* Login Link */}
          <div className="flex-1 flex items-center justify-center">
            <Link 
              to="/auth" 
              className="flex items-center text-sm font-medium text-[#07E5D1] hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-md transition-colors"
              title={isExpanded ? '' : 'Log in'}
            >
              <LogIn className="h-5 w-5" />
              <span className={`ml-3 transition-opacity duration-200 whitespace-nowrap ${isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                Log in
              </span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        className={`md:hidden fixed top-4 left-4 ${getZIndexClass('mobileNav')} p-2 rounded-md bg-white shadow-md border border-gray-200`}
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className={`md:hidden fixed inset-0 bg-black bg-opacity-50 ${getZIndexClass('overlay')}`}
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-[#FAFBFC] border-r border-gray-200 shadow-sm ${getZIndexClass('popover')} transition-all duration-300 ${
        isMobileOpen ? 'w-64 md:w-64' : 'w-0 md:w-16'
      } ${isExpanded && !isMobileOpen ? 'md:w-64' : ''} ${isMobileOpen ? 'block' : 'hidden md:block'}`}
      onMouseEnter={() => !isMobileOpen && setIsExpanded(true)}
      onMouseLeave={() => !isMobileOpen && setIsExpanded(false)}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`border-b border-gray-200 ${isExpanded || isMobileOpen ? 'p-4' : 'px-3 py-4'}`}>
            <div 
              onClick={handleLogoClick} 
              className="flex items-center justify-center cursor-pointer transition-transform hover:scale-105"
            >
              {isExpanded || isMobileOpen ? (
                <img 
                  src={`${import.meta.env.VITE_PUBLIC_URL}/logo.png`} 
                  alt="Bluepill Logo" 
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <div className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                  <img 
                    src={`${import.meta.env.VITE_PUBLIC_URL}/Bluepill Logomark (1).png`} 
                    alt="Bluepill Icon" 
                    className="h-8 w-8 object-contain"
                  />
                </div>
              )}
            </div>
          </div>
          
          {/* Navigation */}
          <nav className={`flex-1 space-y-2 ${isExpanded || isMobileOpen ? 'p-4' : 'px-3 py-4'}`}>
            {navigationItems.map(renderNavItem)}
          </nav>
          
          {/* User Profile Section */}
          <div className={`border-t border-gray-200 ${isExpanded || isMobileOpen ? 'p-4' : 'px-3 py-4'}`}>
            <div className="relative">
              <button
                className={`flex items-center py-2 px-4 transition-all duration-200 group relative ${
                  (isExpanded || isMobileOpen) ? 'justify-start' : 'justify-center'
                }`}
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                title={(isExpanded || isMobileOpen) ? '' : user?.email?.split('@')[0] || ''}
              >
                <div className={`absolute inset-0 rounded-lg transition-all duration-200 bg-gray-50 group-hover:bg-gray-100 ${
                  (isExpanded || isMobileOpen) ? 'opacity-100' : 'opacity-0'
                }`} />
                
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 relative ${getZIndexClass('dropdown')} ${
                  !(isExpanded || isMobileOpen) ? 'bg-gray-50 group-hover:bg-gray-100' : ''
                }`}>
                  <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center text-[#07E5D1]">
                    <User size={14} />
                  </div>
                </div>
                
                <span className={`ml-3 transition-opacity duration-200 truncate whitespace-nowrap relative ${getZIndexClass('dropdown')} text-gray-700 group-hover:text-gray-900 ${(isExpanded || isMobileOpen) ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                  {user?.email?.split('@')[0]}
                </span>
                <ChevronDown className={`ml-1 h-4 w-4 transition-all duration-200 relative ${getZIndexClass('dropdown')} text-gray-700 group-hover:text-gray-900 ${profileMenuOpen ? 'rotate-180' : ''} ${(isExpanded || isMobileOpen) ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`} />
              </button>
              
              {profileMenuOpen && (
                <UserProfileDropdown onClose={() => setProfileMenuOpen(false)} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;